

import * as React from "react";

import { Badge } from "../components/ui/badge";

type BadgeProps = React.ComponentProps<typeof Badge>;

// severityColor / statusColor return a CSS color expression for callers
// that need an inline dot (Pill) or raw color (e.g. swatches). They map to
// the same semantic tokens as the Badge variants to keep the visual
// language consistent.
export function severityColor(s: string): string {
  switch (s) {
    case "critical":
      return "var(--destructive)";
    case "high":
      return "var(--warning)";
    case "medium":
      return "var(--warning)";
    case "low":
      return "var(--primary)";
    default:
      return "var(--muted-foreground)";
  }
}

export function statusColor(s: string): string {
  switch (s) {
    case "new":
      return "var(--info)";
    case "triaged":
    case "in_progress":
      return "var(--primary)";
    case "resolved":
      return "var(--success)";
    case "ignored":
      return "var(--muted-foreground)";
    default:
      return "var(--muted-foreground)";
  }
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "—";
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 45) return "just now";
  if (diff < 60 * 60) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 60 * 60 * 24) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 60 * 60 * 24 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return shortDate(iso);
}

export function formatExact(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

export function Pill({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="inline-block size-1.5 rounded-full" style={{ background: color }} />
      {children}
    </span>
  );
}

// statusBadgeClass returns the semantic className for a status badge.
export function statusBadgeClass(status: string): string {
  switch (status) {
    case "new":
      return "border-info/30 bg-info/10 text-info";
    case "triaged":
    case "in_progress":
      return "border-primary/20 bg-primary/10 text-primary";
    case "resolved":
      return "border-success/30 bg-success/10 text-success";
    case "ignored":
      return "border-border bg-muted text-muted-foreground";
    default:
      return "border-border bg-card text-muted-foreground";
  }
}

// severityBadgeClass returns the semantic className for a severity badge.
export function severityBadgeClass(severity: string): string {
  switch (severity) {
    case "critical":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    case "high":
      return "border-severity-high/30 bg-severity-high/10 text-severity-high";
    case "medium":
      return "border-severity-medium/30 bg-severity-medium/10 text-severity-medium";
    case "low":
      return "border-info/30 bg-info/10 text-info";
    default:
      return "border-border bg-muted text-muted-foreground";
  }
}

// severityDotColor returns the dot color for a severity badge. Used when
// the dot needs to be inline-styled.
export function severityDotColor(s: string): string {
  switch (s) {
    case "critical":
      return "var(--destructive)";
    case "high":
      return "var(--severity-high)";
    case "medium":
      return "var(--severity-medium)";
    case "low":
      return "var(--info)";
    default:
      return "var(--muted-foreground)";
  }
}

// statusDotColor returns the dot color for a status badge.
export function statusDotColor(s: string): string {
  switch (s) {
    case "new":
      return "var(--info)";
    case "resolved":
      return "var(--success)";
    case "triaged":
    case "in_progress":
      return "var(--primary)";
    case "ignored":
      return "var(--muted-foreground)";
    default:
      return "var(--muted-foreground)";
  }
}

// SeverityChip renders a severity value as a shadcn Badge with a colored
// leading dot. It replaces the previous hand-rolled span.
export function SeverityChip({
  severity,
  active,
  ...rest
}: { severity: string; active?: boolean } & Omit<BadgeProps, "variant" | "children">) {
  return (
    <Badge
      variant={severity === "critical" ? "destructive" : "secondary"}
      className={severityBadgeClass(severity)}
      data-testid="severity-chip"
      data-active={active ? "true" : "false"}
      aria-pressed={active ? "true" : "false"}
      title={`Severity: ${severity}`}
      {...rest}
    >
      <span
        className="inline-block size-1.5 rounded-full"
        style={{ background: severityDotColor(severity) }}
        aria-hidden="true"
      />
      {severity || "unknown"}
    </Badge>
  );
}

// StatusChip renders a status value as a shadcn Badge with a colored dot.
export function StatusChip({ status, ...rest }: { status: string } & Omit<BadgeProps, "variant" | "children">) {
  const display = (status || "new").replace("_", " ");
  return (
    <Badge
      variant="secondary"
      className={statusBadgeClass(status)}
      title={`Status: ${status}`}
      {...rest}
    >
      <span
        className="inline-block size-1.5 rounded-full"
        style={{ background: statusDotColor(status) }}
        aria-hidden="true"
      />
      {display}
    </Badge>
  );
}